import React, { useEffect, useCallback } from 'react';
import { Amplify } from 'aws-amplify';
import { awsConfig } from './aws-config';
import { useAuthStore } from 'store/authStore';
import Login from 'components/Login';
import AppHeader from 'components/AppHeader';
import AppSelect from 'components/AppSelect';
import ScanShippingBox from 'components/ScanShippingBox';
import ScanItem from 'components/ScanItem';
import ConfirmItem from 'components/ConfirmItem';
import GradeItem from 'components/GradeItem';
import ConfirmClose from 'components/ConfirmClose';
import { Card } from 'components/ui/card';
import './index.css';
import { SCREEN_TYPES } from 'utils/constants';
import { useAppStore, useTrackingStore } from './store';
import { useProductStore } from './store/productStore';
import { useBarcodeScan } from 'hooks/useBarcodeScan';
import { getBarcodeAction, isInputBarcode } from 'services/barcodes';
import { getFedexId, scanId } from 'services/barcodes/checkFedexId';
import { BarcodeType } from 'types';
import { cn } from './utils/classnames';
import { GradingDisabledScreen } from './components/GradingDisabledScreen';
import { ModeSelectPage } from './components/callback/ModeSelectPage';
import { MoveScanPage } from './components/callback/MovePage/MoveScanPage';
import { MoveDestinationPage } from './components/callback/MovePage/MoveDestinationPage';
import { MoveConfirmPage } from './components/callback/MovePage/MoveConfirmPage';
import { PalletBuildMenuPage } from './components/callback/PalletBuild/PalletBuildMenuPage';
import { CaseScanPage } from './components/callback/PalletBuild/CaseScanPage';
import { AssignProductsPage } from './components/callback/PalletBuild/AssignProductsPage';
import { PalletScanPage } from './components/callback/PalletBuild/PalletScanPage';
import { AssignCasesPage } from './components/callback/PalletBuild/AssignCasesPage';
import { WeighClosePage } from './components/callback/PalletBuild/WeighClosePage';
import { GradingDispositionSheet } from './components/GradingDispositionSheet';
import Receiving from './components/Receiving';

// TODO: barcodeDebugMessage and everything related to this will be removed once we approve barcode features work correctly

Amplify.configure(awsConfig);

const BARCODE_LISTENER_SCREENS = [
  SCREEN_TYPES.GRADE_ITEM,
  SCREEN_TYPES.CONFIRM_ITEM,
  SCREEN_TYPES.CONFIRM_CLOSE,
];

function App() {
  const { status, stationId, restoreSession } = useAuthStore();
  const { currentScreen, setBarcodeDebugMessage } = useAppStore();
  const { setProductNew, setProductCondition, setProductId, setCurrentProduct } = useProductStore();
  const { setCurrentTrackingId } = useTrackingStore();

  const handleBarcodeDetected = useCallback(
    (value: string) => {
      const trimmedValue = value.trim();

      const fedexId = getFedexId(trimmedValue);
      if (fedexId !== trimmedValue) {
        setBarcodeDebugMessage(`FedEx ID detected: ${fedexId}`);
        void scanId(trimmedValue);
        return;
      }

      // Check for other barcode patterns
      const barcodeType = isInputBarcode(trimmedValue) as BarcodeType | null;
      if (barcodeType) {
        setBarcodeDebugMessage(`Barcode detected: ${barcodeType}`);
        getBarcodeAction(barcodeType, currentScreen, {
          setProductNew,
          setProductCondition,
          setBarcodeDebugMessage,
          setProductId,
          setTrackingId: setCurrentTrackingId,
          setCurrentProduct,
        });
      } else {
        setBarcodeDebugMessage(
          `Input not recognized as FedEx, tracking ID, or barcode. Input -> ${trimmedValue}`
        );
        console.log('Input not recognized as FedEx, tracking ID, or barcode:', trimmedValue);
      }
    },
    [
      currentScreen,
      setProductNew,
      setProductCondition,
      setBarcodeDebugMessage,
      setProductId,
      setCurrentProduct,
      setCurrentTrackingId,
    ]
  );

  useBarcodeScan({
    onBarcode: handleBarcodeDetected,
    enabled: BARCODE_LISTENER_SCREENS.includes(currentScreen as any),
    setBarcodeDebugMessage,
  });

  useEffect(() => {
    void restoreSession();
  }, [restoreSession]);

  if (status === 'loading') {
    return <div className="app-container">Loading...</div>;
  }

  if (status !== 'authenticated' || !stationId) {
    return (
      <div className="app-container">
        <Login />
      </div>
    );
  }

  const getCurrentScreenComponent = (): React.ReactNode => {
    switch (currentScreen) {
      case SCREEN_TYPES.APP_SELECT:
        return <AppSelect />;
      case SCREEN_TYPES.SCAN_BOX:
        return <ScanShippingBox />;
      case SCREEN_TYPES.SCAN_ITEM:
        return <ScanItem />;
      case SCREEN_TYPES.CONFIRM_ITEM:
        return <ConfirmItem />;
      case SCREEN_TYPES.GRADE_ITEM:
        return <GradeItem />;
      case SCREEN_TYPES.CONFIRM_CLOSE:
        return <ConfirmClose />;
      case SCREEN_TYPES.RECEIVING:
        return <Receiving />;
      case SCREEN_TYPES.GRADING_DISABLED:
        return <GradingDisabledScreen />;
      case SCREEN_TYPES.CALLBACK:
        return <ModeSelectPage />;
      case SCREEN_TYPES.MOVE_SCAN:
        return <MoveScanPage />;
      case SCREEN_TYPES.MOVE_DESTINATION:
        return <MoveDestinationPage />;
      case SCREEN_TYPES.MOVE_CONFIRM:
        return <MoveConfirmPage />;
      case SCREEN_TYPES.PALLET_BUILD:
        return <PalletBuildMenuPage />;
      case SCREEN_TYPES.PALLET_BUILD_CASE_SCAN:
        return <CaseScanPage />;
      case SCREEN_TYPES.PALLET_BUILD_ASSIGN_PRODUCTS:
        return <AssignProductsPage />;
      case SCREEN_TYPES.PALLET_BUILD_PALLET_SCAN:
        return <PalletScanPage />;
      case SCREEN_TYPES.PALLET_BUILD_ASSIGN_CASES:
        return <AssignCasesPage />;
      case SCREEN_TYPES.PALLET_BUILD_WEIGH_CLOSE:
        return <WeighClosePage />;

      default:
        return <AppSelect />;
    }
  };

  return (
    <div className="app-container">
      <div className="w-full max-w-[860px]">
        <Card
          className={cn(
            'scanner-card flex flex-col overflow-hidden rounded-3xl bg-white px-0 py-0 shadow-[0_10px_40px_rgba(0,0,0,0.1)]'
          )}
        >
          <AppHeader />
          {/* <CardHeader className="bg-slate-100">{barcodeDebugMessage}</CardHeader> */}
          <div className={cn('flex-1 min-h-0 overflow-y-auto px-4 py-4')}>
            {getCurrentScreenComponent()}
          </div>
          <GradingDispositionSheet />
        </Card>
      </div>
    </div>
  );
}

export default App;
